import { types as sdkTypes } from './sdkLoader';
import { packingClassForListingType } from './freeShippingThreshold';
import { validateShippingZones } from './shippingRates';

const { Money } = sdkTypes;

const feeAmount = value => {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
    return value;
  }
  if (
    value &&
    typeof value.amount === 'number' &&
    Number.isInteger(value.amount) &&
    value.amount >= 0
  ) {
    return value.amount;
  }
  return null;
};

const hasRatesShape = value =>
  !!value && typeof value === 'object' && value.default && typeof value.default === 'object';

const normalizeZones = zones =>
  Array.isArray(zones)
    ? zones.map(zone => ({
        name: typeof zone?.name === 'string' ? zone.name : '',
        states: Array.isArray(zone?.states)
          ? zone.states.filter(code => typeof code === 'string')
          : [],
        countries: Array.isArray(zone?.countries)
          ? zone.countries.filter(code => typeof code === 'string')
          : [],
        shippingPriceInSubunitsOneItem: feeAmount(zone?.shippingPriceInSubunitsOneItem),
        shippingPriceInSubunitsAdditionalItems: feeAmount(
          zone?.shippingPriceInSubunitsAdditionalItems
        ),
      }))
    : [];

export const getProfileShippingRates = (user, packingClass) => {
  const publicData = user?.attributes?.profile?.publicData || {};
  if (
    packingClass === 'live' &&
    publicData.shippingRatesLiveEnabled === true &&
    hasRatesShape(publicData.shippingRatesLive)
  ) {
    return publicData.shippingRatesLive;
  }
  if (
    packingClass === 'dry' &&
    publicData.shippingRatesDryEnabled === true &&
    hasRatesShape(publicData.shippingRatesDry)
  ) {
    return publicData.shippingRatesDry;
  }
  return null;
};

export const isProfileShippingRatesComplete = rates => {
  if (!hasRatesShape(rates)) {
    return false;
  }
  const first = feeAmount(rates.default.shippingPriceInSubunitsOneItem);
  const additional = feeAmount(rates.default.shippingPriceInSubunitsAdditionalItems);
  if (first == null || additional == null) {
    return false;
  }
  return validateShippingZones(normalizeZones(rates.zones)).valid;
};

export const copyProfileShippingRatesToDeliveryValues = (rates, currency) => ({
  shippingPriceInSubunitsOneItem: new Money(rates.default.shippingPriceInSubunitsOneItem, currency),
  shippingPriceInSubunitsAdditionalItems: new Money(
    rates.default.shippingPriceInSubunitsAdditionalItems,
    currency
  ),
  zones: normalizeZones(rates.zones),
});

export const canCopyProfileShippingRates = (user, listingType) => {
  const packingClass = packingClassForListingType(listingType);
  if (!packingClass) {
    return false;
  }
  return isProfileShippingRatesComplete(getProfileShippingRates(user, packingClass));
};

export const buildProfileShippingRatesFromFormClass = ({ enabled, first, additional, zones }) => {
  if (enabled !== true) {
    return { enabled: false, rates: null };
  }
  return {
    enabled: true,
    rates: {
      default: {
        shippingPriceInSubunitsOneItem: feeAmount(first),
        shippingPriceInSubunitsAdditionalItems: feeAmount(additional),
      },
      zones: normalizeZones(zones),
    },
  };
};

export const validateProfileShippingClass = ({
  enabled,
  first,
  additional,
  zones,
  feeRequiredMessage,
}) => {
  if (enabled !== true) {
    return {};
  }
  const errors = {};
  if (feeAmount(first) == null) {
    errors.first = feeRequiredMessage;
  }
  if (feeAmount(additional) == null) {
    errors.additional = feeRequiredMessage;
  }
  const normalized = normalizeZones(zones);
  const zoneResult = validateShippingZones(normalized);
  if (!zoneResult.valid) {
    errors.zoneErrors = zoneResult.errors;
  }
  return errors;
};
